import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import { ButtonTypes, IconSizes } from 'constants/Constants';
import ToolTip from 'components/common/ToolTip/ToolTip';
import getIconFromConstant from './getIconFromConstant';

import styles from './Icon.module.scss';

const s = classNames.bind(styles);

const Icon = ({
	icon: iconConstant,
	size,
	fixedIconColor,
	rotateIcon,
	rotateIconByDeg,
	color,
	marginRight,
	disabled,
	active,
	className,
	tooltip,
	...other
}) => {
	if (!iconConstant) {
		return null;
	}

	/**
	 * @function getIconClassName
	 * @param additionalClassName {string} = icon-specific class name, set in getIconFromConstant.
	 * @returns {classNames}
	 */
	const getIconClassName = additionalClassName =>
		s('icon', {
			icon_hoverColor: !fixedIconColor,
			icon_rotate: rotateIcon,
			[`icon_rotate${rotateIconByDeg}`]: rotateIcon && rotateIconByDeg,
			icon_auto: size === IconSizes.AUTO,
			icon_small: size === IconSizes.SMALL,
			icon_medium: size === IconSizes.MEDIUM,
			icon_large: size === IconSizes.LARGE,
			icon_primary: color === ButtonTypes.PRIMARY,
			icon_secondary: color === ButtonTypes.SECONDARY,
			icon_dark: color === ButtonTypes.DARK,
			icon_white: color === ButtonTypes.WHITE,
			icon_error: color === ButtonTypes.ERROR,
			icon_currentColor: color === 'currentColor',
			icon_disabled: disabled,
			icon_marginRight: marginRight,
			icon_active: active,
			[additionalClassName]: additionalClassName,
			[className]: className
		});

	/**
	 * @function getIcon
	 * @param iconConstant {string} = The icon constant from Constants.IconTypes
	 * @param props = spread ...other props
	 * @returns Matched icon component node with applied classNames and spread props
	 */
	const getIcon = (iconConstant, props) => {
		if (!iconConstant) {
			return null;
		}

		if (tooltip) {
			return <ToolTip text={tooltip}>{getIconFromConstant(iconConstant, getIconClassName, props)}</ToolTip>;
		}
		return getIconFromConstant(iconConstant, getIconClassName, props);
	};

	return getIcon(iconConstant, other);
};

Icon.defaultProps = {
	color: ButtonTypes.SECONDARY
};

Icon.propTypes = {
	// The icon constant from Constants.IconTypes
	icon: PropTypes.string,
	// Set to true if the icon color shouldn't change on hover
	fixedIconColor: PropTypes.bool,
	// Set to true to rotate the icon. Note that this is dependant on the icon having an attached additional class inside getIconFromConstant
	// specific to the icon, meaning each icon has specific rotational options
	rotateIcon: PropTypes.bool,
	// Set to a number to rotate the icon by a number of degrees. Note: Requires rotateIcon to be set to true.
	rotateIconByDeg: PropTypes.oneOf([45, 90, 180, 270]),
	// The color constant from Constants.ButtonTypes
	color: PropTypes.oneOf([
		ButtonTypes.PRIMARY,
		ButtonTypes.SECONDARY,
		ButtonTypes.DARK,
		ButtonTypes.WHITE,
		ButtonTypes.CURRENT,
		ButtonTypes.ERROR
	]),
	// The size constant from Constants.IconSizes: 100%x100%, 18x18, 24x24 or 40x40
	size: PropTypes.oneOf([IconSizes.AUTO, IconSizes.SMALL, IconSizes.MEDIUM, IconSizes.LARGE]),
	// Set to true to have a 8px margin to the right
	marginRight: PropTypes.bool,
	// Set to true to set disabled state ($text-disabled color)
	disabled: PropTypes.bool,
	// Set to true to set active state ($blue color)
	active: PropTypes.bool,
	// Used to customize the icon presentation from parents
	className: PropTypes.string,
	// Display a tooltip with the privided text on hover
	tooltip: PropTypes.string
};

export default React.memo(Icon);
